'use client';

/**
 * SearchableMultiSelect — search box + removable selected chips.
 *
 * Two data sources, picked by the caller:
 *  - a static `options` list (e.g. regions) filtered client-side, OR
 *  - an async `onSearch(query)` fetcher (e.g. creator lookup) whose results
 *    populate the dropdown.
 *
 * Selected entries render as removable chips below the input. The result list
 * is keyboard-navigable; selecting adds to the controlled `value` array,
 * clicking a chip's ✕ removes it.
 */
import { c as _c } from "react/compiler-runtime";
import type { ChangeEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Cancel01Icon, Search02Icon } from '@/components/ui/icon/glyphs';
import { cn } from '@/lib/ui/utils';
export interface MultiSelectOption {
  value: string;
  label: string;
  emoji?: string;
}
export interface SearchableMultiSelectProps {
  /** Currently selected entries (controlled). Carries label/emoji so chips render without a lookup. */
  value: MultiSelectOption[];
  onChange: (next: MultiSelectOption[]) => void;
  placeholder: string;
  /** Static option list — filtered client-side by label. Mutually used with or without `onSearch`. */
  options?: MultiSelectOption[] | undefined;
  /** Async fetcher. When provided, drives the dropdown instead of `options`. */
  onSearch?: ((query: string) => Promise<MultiSelectOption[]>) | undefined;
  /**
   * Lifts the search query out of internal state so a parent can drive an
   * SDK-backed reactive search (e.g. creator lookup via `useGetSearch`) and
   * feed results back through `options`. When set, the parent owns the query
   * value and `onQueryChange` is the only writer. Mutually exclusive with the
   * internal-state default; `onSearch` is ignored while controlled.
   */
  controlledQuery?: string | undefined;
  onQueryChange?: ((query: string) => void) | undefined;
  /** Empty-results copy. */
  emptyLabel?: string | undefined;
  className?: string | undefined;
}
function normalize(s: string): string {
  return s.toLowerCase().trim();
}
export function SearchableMultiSelect(t0) {
  const $ = _c(62);
  const {
    value,
    onChange,
    placeholder,
    options,
    onSearch,
    controlledQuery,
    onQueryChange,
    emptyLabel: t1,
    className
  } = t0;
  const emptyLabel = t1 === undefined ? "No results" : t1;
  const isControlled = controlledQuery !== undefined;
  const [internalQuery, setInternalQuery] = useState("");
  const query = isControlled ? controlledQuery : internalQuery;
  let t2;
  if ($[0] !== isControlled || $[1] !== onQueryChange) {
    t2 = function setQuery(next) {
      if (isControlled) {
        onQueryChange?.(next);
      } else {
        setInternalQuery(next);
      }
    };
    $[0] = isControlled;
    $[1] = onQueryChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const setQuery = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [asyncResults, setAsyncResults] = useState(t3);
  const [focused, setFocused] = useState(false);
  const requestIdRef = useRef(0);
  let t4;
  if ($[4] !== value) {
    t4 = new Set(value.map(_temp));
    $[4] = value;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const selectedValues = t4;
  let t5;
  let t6;
  if ($[6] !== onSearch || $[7] !== query) {
    t5 = () => {
      if (onSearch === undefined) {
        return;
      }
      const q = normalize(query);
      if (q === "") {
        setAsyncResults([]);
        return;
      }
      const id = requestIdRef.current + 1;
      requestIdRef.current = id;
      onSearch(q).then(results => {
        if (requestIdRef.current === id) {
          setAsyncResults(results);
        }
      });
    };
    t6 = [query, onSearch];
    $[6] = onSearch;
    $[7] = query;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let remove;
  let t10;
  let t11;
  let t7;
  let t8;
  let t9;
  if ($[10] !== asyncResults || $[11] !== className || $[12] !== emptyLabel || $[13] !== focused || $[14] !== isControlled || $[15] !== onChange || $[16] !== onSearch || $[17] !== options || $[18] !== placeholder || $[19] !== query || $[20] !== selectedValues || $[21] !== setQuery || $[22] !== value) {
    const results_0 = isControlled ? options ?? [] : onSearch !== undefined ? asyncResults : (options ?? []).filter(o => normalize(o.label).includes(normalize(query)));
    let t12;
    if ($[29] !== selectedValues) {
      t12 = o_0 => !selectedValues.has(o_0.value);
      $[29] = selectedValues;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    const visibleResults = results_0.filter(t12);
    const showDropdown = focused && (query !== "" || onSearch === undefined && !isControlled);
    let t13;
    if ($[31] !== onChange || $[32] !== selectedValues || $[33] !== setQuery || $[34] !== value) {
      t13 = function add(option) {
        if (selectedValues.has(option.value)) {
          return;
        }
        onChange([...value, option]);
        setQuery("");
      };
      $[31] = onChange;
      $[32] = selectedValues;
      $[33] = setQuery;
      $[34] = value;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    const add = t13;
    let t14;
    if ($[36] !== onChange || $[37] !== value) {
      t14 = function remove(optionValue) {
        onChange(value.filter(v_0 => v_0.value !== optionValue));
      };
      $[36] = onChange;
      $[37] = value;
      $[38] = t14;
    } else {
      t14 = $[38];
    }
    remove = t14;
    t10 = "searchable-multi-select";
    if ($[39] !== className) {
      t11 = cn("flex flex-col gap-2", className);
      $[39] = className;
      $[40] = t11;
    } else {
      t11 = $[40];
    }
    t7 = "relative";
    let t15;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <Icon icon={Search02Icon} className="size-4 shrink-0 text-foreground-tertiary" />;
      $[41] = t15;
    } else {
      t15 = $[41];
    }
    let t16;
    if ($[42] !== setQuery) {
      t16 = e => setQuery(e.target.value);
      $[42] = setQuery;
      $[43] = t16;
    } else {
      t16 = $[43];
    }
    let t17;
    let t18;
    if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = () => setFocused(true);
      t18 = () => {
        window.setTimeout(() => setFocused(false), 120);
      };
      $[44] = t17;
      $[45] = t18;
    } else {
      t17 = $[44];
      t18 = $[45];
    }
    if ($[46] !== placeholder || $[47] !== query || $[48] !== t16) {
      t8 = <div className="flex h-10 items-center gap-2 rounded-control border border-border-primary bg-background-secondary px-3">{t15}<input value={query} onChange={t16} onFocus={t17} onBlur={t18} placeholder={placeholder} aria-label={placeholder} className="h-full w-full bg-transparent text-body text-foreground-primary outline-none placeholder:text-foreground-tertiary" type="text" autoComplete="off" autoCorrect="off" spellCheck={false} /></div>;
      $[46] = placeholder;
      $[47] = query;
      $[48] = t16;
      $[49] = t8;
    } else {
      t8 = $[49];
    }
    t9 = showDropdown && <div role="listbox" className="absolute inset-x-0 top-full z-popover mt-1 max-h-60 overflow-y-auto overscroll-contain rounded-overlay border bg-surface-floating p-1 shadow-elevation-popover">{visibleResults.length === 0 ? <div className="py-4 text-center text-body text-foreground-secondary">{emptyLabel}</div> : visibleResults.map(option_0 => <MultiSelectRow key={option_0.value} option={option_0} onSelect={() => add(option_0)} />)}</div>;
    $[10] = asyncResults;
    $[11] = className;
    $[12] = emptyLabel;
    $[13] = focused;
    $[14] = isControlled;
    $[15] = onChange;
    $[16] = onSearch;
    $[17] = options;
    $[18] = placeholder;
    $[19] = query;
    $[20] = selectedValues;
    $[21] = setQuery;
    $[22] = value;
    $[23] = remove;
    $[24] = t10;
    $[25] = t11;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    remove = $[23];
    t10 = $[24];
    t11 = $[25];
    t7 = $[26];
    t8 = $[27];
    t9 = $[28];
  }
  let t12;
  if ($[50] !== t7 || $[51] !== t8 || $[52] !== t9) {
    t12 = <div className={t7}>{t8}{t9}</div>;
    $[50] = t7;
    $[51] = t8;
    $[52] = t9;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  let t13;
  if ($[54] !== remove || $[55] !== value) {
    t13 = value.length > 0 && <div className="flex flex-wrap gap-1.5">{value.map(option_1 => <AnimatedButton key={option_1.value} variant="tertiary" size="sm" disableHoverScale={true} onClick={() => remove(option_1.value)} aria-label={`Remove ${option_1.label}`} className="h-auto rounded-pill border border-border-brand bg-background-brand px-3 py-1.5 text-caption font-medium text-foreground-brand">{option_1.emoji !== undefined && <span aria-hidden="true" className="mr-1">{option_1.emoji}</span>}{option_1.label}<Icon icon={Cancel01Icon} className="ml-1 size-3 text-foreground-brand" /></AnimatedButton>)}</div>;
    $[54] = remove;
    $[55] = value;
    $[56] = t13;
  } else {
    t13 = $[56];
  }
  let t14;
  if ($[57] !== t10 || $[58] !== t11 || $[59] !== t12 || $[60] !== t13) {
    t14 = <div data-slot={t10} className={t11}>{t12}{t13}</div>;
    $[57] = t10;
    $[58] = t11;
    $[59] = t12;
    $[60] = t13;
    $[61] = t14;
  } else {
    t14 = $[61];
  }
  return t14;
}
function _temp(v) {
  return v.value;
}
interface MultiSelectRowProps {
  option: MultiSelectOption;
  onSelect: () => void;
}
function MultiSelectRow(t0) {
  const $ = _c(11);
  const {
    option,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== onSelect) {
    t1 = e => {
      e.preventDefault();
      onSelect();
    };
    $[0] = onSelect;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("flex w-full items-center gap-2 rounded-overlay-item px-2 py-1.5 text-body outline-hidden", "cursor-default select-none transition-colors hover:bg-background-tertiary");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== option.emoji) {
    t3 = option.emoji !== undefined && <span aria-hidden="true" className="shrink-0">{option.emoji}</span>;
    $[3] = option.emoji;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== option.label) {
    t4 = <span className="truncate text-left text-foreground-primary">{option.label}</span>;
    $[5] = option.label;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t3 || $[9] !== t4) {
    t5 = <button type="button" role="option" aria-selected={false} onPointerDown={t1} className={t2}>{t3}{t4}</button>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}